'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import {
  nativeAwareSignIn,
  nativeAwareSignUp,
  nativeAwareSignInWithGoogle,
  nativeAwareSignOut,
} from './nativeAuth';
import { initializeRevenueCat, identifyUser, logoutUser } from './revenuecat';
import { isBetaPeriodActive } from './betaTesters';
import { checkBetaCapacity, claimBetaSlot } from './betaSignup';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Wrap a promise with a timeout. Rejects with the given message if the
 * wrapped promise doesn't settle in `ms` milliseconds. Used to keep auth
 * calls from hanging the UI on iOS 26 WebView + Firestore IndexedDB
 * stalls (App Store rejection 2.1(a), 2026-05-30).
 */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise
      .then(v => { clearTimeout(timer); resolve(v); })
      .catch(e => { clearTimeout(timer); reject(e); });
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Stamp wake-time on initial mount for web users (who never fire
  // appStateChange). Idempotent within a day — only the first call
  // each calendar day actually records. Used by Pillar Challenges'
  // wake-by-time commitment.
  useEffect(() => {
    (async () => {
      try {
        const { stampWake } = await import('./pillarChallengesEngine');
        stampWake();
      } catch { /* non-critical */ }
    })();
  }, []);

  // App-resume hook for the native shell. When the user backgrounds
  // OPMODE and reopens it later (very common workflow — Garmin Connect
  // syncs, then user comes back to check recovery), fire a fresh
  // health-data sync. Without this, the snapshot panel stays stale
  // until the user pulls-to-refresh or relaunches.
  //
  // Debounced to once per 2 minutes — if iOS rapidly fires
  // foreground/background transitions (it does, especially during
  // sheet dismissals), we don't want to thrash the HC permission
  // pipeline.
  useEffect(() => {
    if (!user?.uid) return;
    let unsubscribe: (() => void) | null = null;
    let lastResumeSync = 0;
    const RESUME_COOLDOWN_MS = 2 * 60 * 1000;

    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return; // browser handles via tab visibility events elsewhere
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', async ({ isActive }) => {
          if (!isActive) return;
          // Always stamp wake-time on EVERY foreground event (cheap,
          // idempotent within a day). Pillar Challenges' wake-by-time
          // commitment reads this. We want the FIRST app-open of the day
          // to count; stampWake() no-ops after the first stamp.
          try {
            const { stampWake } = await import('./pillarChallengesEngine');
            stampWake();
          } catch { /* non-critical */ }

          const elapsed = Date.now() - lastResumeSync;
          if (elapsed < RESUME_COOLDOWN_MS) return;
          lastResumeSync = Date.now();

          // Re-arm Pillar Challenge reminders. Local notifications are
          // queued for TODAY only, so a user who opened the app on day N
          // and came back on day N+2 has an empty queue. Fire-and-forget,
          // behind the same 2-minute cooldown as the health sync;
          // scheduleChallengeNotifications self-throttles on top of that
          // (identical inputs inside 10 min are a no-op) and returns
          // immediately on web.
          void (async () => {
            try {
              const { getActiveChallenge, getDailyStatus } = await import('./pillarChallengesEngine');
              const challenge = await getActiveChallenge(user.uid);
              if (!challenge || challenge.status !== 'active') return;
              const now = new Date();
              const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
              const status = await getDailyStatus(user.uid, challenge.id, today);
              const { scheduleChallengeNotifications } = await import('./pillarChallengeNotifications');
              await scheduleChallengeNotifications(challenge, status);
            } catch (err) {
              console.warn('[auth] app-resume challenge reschedule failed:', err);
            }
          })();

          try {
            const { syncHealthData } = await import('./healthData');
            const synced = await syncHealthData(user.uid);
            console.log('[auth] app-resume health sync:', synced ? 'ok' : 'no-op');
          } catch (err) {
            console.warn('[auth] app-resume health sync failed:', err);
          }
        });
        unsubscribe = () => { handle.remove(); };
      } catch (err) {
        // Pure web — @capacitor/app not present, no-op
      }
    })();

    return () => { if (unsubscribe) unsubscribe(); };
  }, [user?.uid]);

  useEffect(() => {
    // Safety timeout - don't hang forever
    const timeout = setTimeout(() => {
      console.warn('Auth initialization timeout - continuing anyway');
      setLoading(false);
    }, 5000); // 5 second max wait
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timeout);
      setUser(user);
      setLoading(false); // Unblock rendering IMMEDIATELY

      // Everything below runs in the background — no await blocking the UI
      if (user) {
        (async () => {
          try {
            const userRef = doc(db, 'users', user.uid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
              const newUserData: Record<string, any> = {
                email: user.email,
                displayName: user.displayName || 'Operator',
                createdAt: new Date(),
                lastLogin: new Date(),
                // New users are NOT beta testers — beta access is admin-granted only
              };

              await setDoc(userRef, newUserData);
            } else {
              // Update last login (non-blocking)
              setDoc(userRef, { lastLogin: new Date() }, { merge: true }).catch(() => {});
            }

            // Auto-detect timezone from the browser and persist if missing
            // or if it has drifted from what's on file. The server-side push
            // pipeline reads users/{uid}.timezone to fire at each user's
            // local 8 PM / 7 PM / etc. (lib/notificationPrefs.ts).
            try {
              const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              if (browserTz) {
                const storedTz = userSnap.exists() ? (userSnap.data() as { timezone?: string })?.timezone : undefined;
                if (storedTz !== browserTz) {
                  setDoc(userRef, { timezone: browserTz }, { merge: true }).catch(() => {});
                }
              }
            } catch { /* non-critical */ }

            // RevenueCat full init (with Stripe) stays deferred until a
            // paywall opens — pulling Stripe into the global bundle was
            // breaking Next.js's error overlay handler.
            //
            // But every authenticated user should exist in the
            // RevenueCat dashboard, not just paywall-openers. The
            // lightweight identity ping below registers the user via the
            // REST API on web (no Stripe load) or the Capacitor SDK on
            // native (no Stripe involved).
            try {
              const { registerUserWithRevenueCat } = await import('./revenuecatIdentity');
              await registerUserWithRevenueCat(user.uid);
            } catch (rcErr) {
              console.warn('RevenueCat identity register failed (non-critical):', rcErr);
            }
          } catch (error) {
            console.error('Error setting up user profile:', error);
          }
        })();
      } else {
        import('./revenuecatIdentity')
          .then(({ resetRevenueCatRegistration }) => resetRevenueCatRegistration())
          .catch(() => { /* non-critical */ });
        logoutUser().catch(() => {});
      }
    });

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // Path B fix (June 2026): native-aware wrapper routes through the
      // native Firebase iOS SDK on Capacitor (via @capacitor-firebase/
      // authentication) and the JS SDK on web. Native iOS SDK bypasses
      // the WKWebView issues (ITP 7-day storage eviction, capacitor://
      // localhost origin blocked, signInWithPopup broken) that caused
      // Apple App Store review rejection 8560ac5f. The 20s timeout
      // wrapper still applies as defence-in-depth — even native calls
      // shouldn't hang forever.
      await withTimeout(
        nativeAwareSignIn(email, password),
        13_000,
        'Sign-in is taking longer than expected — check your connection and tap Sign In again.'
      );
    } catch (error: any) {
      // Preserve error.code so the login page can surface the specific
      // Firebase error (auth/user-not-found, auth/wrong-password,
      // auth/network-request-failed, auth/too-many-requests, etc.).
      const err = new Error(error?.code ? `${error.code}: ${error.message}` : error?.message || 'Sign-in failed');
      (err as any).code = error?.code;
      throw err;
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      // Path B (June 2026): same native-aware migration as signIn.
      // nativeAwareSignUp returns a Firebase JS SDK User after
      // bridging through the native iOS SDK on Capacitor.
      const user = await withTimeout(
        nativeAwareSignUp(email, password),
        15_000,
        'Sign-up is taking longer than expected — check your connection and try again.'
      );

      // updateProfile + setDoc are non-critical for navigation. Fire them
      // without awaiting so iOS 26 WebView Firestore IndexedDB stalls
      // can't block the redirect to onboarding. Failures are logged and
      // self-heal on the next session sync.
      const profileWrites = (async () => {
        try {
          await updateProfile(user, { displayName });
        } catch (e) {
          console.warn('[auth] updateProfile failed (non-fatal):', e);
        }
        try {
          await setDoc(doc(db, 'users', user.uid), {
            email,
            displayName,
            createdAt: new Date(),
            lastLogin: new Date(),
            termsAcceptedAt: new Date(),
            consecutiveSurveyMisses: 0,
            pendingSurvey: null,
            lastSurveyCompleted: null,
          });
        } catch (e) {
          console.warn('[auth] user profile write failed (will retry on next login):', e);
        }
      })();
      // Give Firestore a brief window to land the write while still on
      // good network, but DON'T let it stall the UI. 5s is enough for a
      // healthy device, short enough that the user gets to onboarding
      // fast on a flaky connection.
      await Promise.race([
        profileWrites,
        new Promise(resolve => setTimeout(resolve, 5_000)),
      ]);
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Path B: nativeAwareSignInWithGoogle uses the plugin's native
      // OAuth flow on iOS (avoids the broken signInWithPopup in
      // WKWebView) and the JS SDK popup on web.
      const user = await withTimeout(
        nativeAwareSignInWithGoogle(),
        20_000,
        'Google sign-in is taking longer than expected — check your connection and try again.'
      );

      // Defer profile creation off the critical path. The onAuthStateChanged
      // listener in this same provider creates the profile if missing.
      (async () => {
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          if (!userSnap.exists()) {
            await setDoc(userRef, {
              email: user.email,
              displayName: user.displayName || 'Operator',
              createdAt: new Date(),
              lastLogin: new Date(),
            });
          }
        } catch (e) {
          console.warn('[auth] Google profile bootstrap failed (non-fatal):', e);
        }
      })();
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const signOut = async () => {
    try {
      // Logout from RevenueCat first
      try {
        await logoutUser();
      } catch (error) {
        console.error(' RevenueCat logout failed:', error);
      }
      
      // Sign out from BOTH the native plugin AND the JS SDK so neither
      // holds a stale session. Native-only sign-out would leave the JS
      // SDK thinking the user is still authenticated.
      await nativeAwareSignOut();
      
      // THEN clear localStorage to prevent data leakage between users
      // (Firebase auth tokens are already cleared by firebaseSignOut)
      localStorage.clear();
      sessionStorage.clear();
      // Also clear Dexie (IndexedDB) to prevent leakage between users
      try {
        const { clearDatabase } = await import('./hooks/useDexieInit');
        await clearDatabase();
      } catch (e) {
        console.warn('Dexie clear failed (non-fatal):', e);
      }
      console.log('� Cleared all local storage after logout');
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

